import React, { useEffect, useState } from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Paper from '@mui/material/Paper';
import IconButton from '@mui/material/IconButton';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import TextField from '@mui/material/TextField';
import moment from 'moment-jalaali';
import toast from 'react-hot-toast';
import { TimePicker } from '@mui/x-date-pickers/TimePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
import Fab from '@mui/material/Fab';
import EventIcon from '@mui/icons-material/Event';
import Tooltip from '@mui/material/Tooltip';
import { getEndpoint } from '../config';

// تنظیم moment برای استفاده از تقویم شمسی
moment.loadPersian({ dialect: 'persian-modern' });

function Events() {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [editEvent, setEditEvent] = useState(null);
  const [form, setForm] = useState({ 
    title: '', 
    event_year: moment().jYear(),
    event_month: moment().jMonth() + 1,
    event_day: moment().jDate(),
    event_time: moment().hour(moment().hour()).minute(moment().minute()),
    location: '', 
    description: '', 
    registration_link: '',
    meta_title: '',
    meta_description: '',
    meta_keywords: ''
  });
  const [saving, setSaving] = useState(false);
  const [posterFile, setPosterFile] = useState(null);

  useEffect(() => {
    fetchEvents();
  }, []);

  const fetchEvents = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      
      if (!token) {
        toast.error('توکن احراز هویت یافت نشد. لطفاً دوباره وارد شوید.');
        window.location.href = '/login';
        return;
      }
      
      const res = await fetch(getEndpoint('EVENTS'), {
        headers: { 'Authorization': 'Bearer ' + token }
      });
      
      if (res.status === 401) {
        toast.error('توکن منقضی شده است. لطفاً دوباره وارد شوید.');
        localStorage.removeItem('token');
        window.location.href = '/login';
        return;
      } else if (res.status === 403) {
        toast.error('توکن نامعتبر است. لطفاً دوباره وارد شوید.');
        localStorage.removeItem('token');
        window.location.href = '/login';
        return;
      }
      
      const data = await res.json();
      setEvents(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error('Error fetching events:', err);
      toast.error('خطا در دریافت رویدادها');
      setEvents([]);
    }
    setLoading(false);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('آیا از حذف این رویداد اطمینان دارید؟')) return;
    
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${getEndpoint('EVENTS')}/${id}`, {
        method: 'DELETE',
        headers: { 'Authorization': 'Bearer ' + token }
      });
      
      if (res.ok) {
        toast.success('رویداد با موفقیت حذف شد');
        fetchEvents();
      } else {
        const error = await res.json();
        toast.error(error.error || 'خطا در حذف رویداد');
      }
    } catch (err) {
      console.error('Error deleting event:', err);
      toast.error('خطا در حذف رویداد');
    }
  };

  const handleOpenModal = (event = null) => {
    if (event) {
      const eventMoment = moment(event.event_date);
      setEditEvent(event);
      setForm({
        title: event.title || '',
        event_year: eventMoment.jYear(),
        event_month: eventMoment.jMonth() + 1,
        event_day: eventMoment.jDate(),
        event_time: moment().hour(eventMoment.hour()).minute(eventMoment.minute()),
        location: event.location || '',
        description: event.description || '',
        registration_link: event.registration_link || '',
        meta_title: event.meta_title || '',
        meta_description: event.meta_description || '',
        meta_keywords: event.meta_keywords || ''
      });
    } else {
      setEditEvent(null);
      setForm({
        title: '',
        event_year: moment().jYear(),
        event_month: moment().jMonth() + 1,
        event_day: moment().jDate(),
        event_time: moment().hour(moment().hour()).minute(moment().minute()),
        location: '',
        description: '',
        registration_link: '',
        meta_title: '',
        meta_description: '',
        meta_keywords: ''
      });
    }
    setPosterFile(null);
    setModalOpen(true);
  };

  const handleCloseModal = () => {
    setModalOpen(false);
    setEditEvent(null);
    setPosterFile(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    
    // Validation
    if (!form.title || !form.location) {
      toast.error('عنوان و مکان رویداد الزامی است');
      setSaving(false);
      return;
    }
    
    if (form.event_year < 1400 || form.event_year > 1500) {
      toast.error('سال باید بین 1400 تا 1500 باشد');
      setSaving(false);
      return;
    }
    
    if (form.event_month < 1 || form.event_month > 12) {
      toast.error('ماه باید بین 1 تا 12 باشد');
      setSaving(false);
      return;
    }
    
    if (form.event_day < 1 || form.event_day > 31) {
      toast.error('روز باید بین 1 تا 31 باشد');
      setSaving(false);
      return;
    }
    
    // بررسی معتبر بودن تاریخ
    const testDate = moment().jYear(form.event_year)
      .jMonth(form.event_month - 1)
      .jDate(form.event_day)
      .hour(form.event_time.hour())
      .minute(form.event_time.minute())
      .second(0);
    
    if (!testDate.isValid()) {
      toast.error('تاریخ وارد شده معتبر نیست');
      setSaving(false);
      return;
    }
    
    try {
      const token = localStorage.getItem('token');
      const formData = new FormData();
      
      // ساخت تاریخ از فیلدهای جداگانه (ذخیره به صورت local time)
      const eventDate = moment().jYear(form.event_year)
        .jMonth(form.event_month - 1)
        .jDate(form.event_day)
        .hour(form.event_time.hour())
        .minute(form.event_time.minute())
        .second(0);
      
      formData.append('title', form.title);
      formData.append('event_date', eventDate.format('YYYY-MM-DD HH:mm:ss'));
      formData.append('location', form.location);
      formData.append('description', form.description);
      formData.append('registration_link', form.registration_link);
      formData.append('meta_title', form.meta_title);
      formData.append('meta_description', form.meta_description);
      formData.append('meta_keywords', form.meta_keywords);
      
      if (posterFile) {
        formData.append('poster', posterFile);
      }
      
      const url = editEvent ? `${getEndpoint('EVENTS')}/${editEvent.id}` : getEndpoint('EVENTS');
      const method = editEvent ? 'PUT' : 'POST';
      
      const res = await fetch(url, {
        method,
        headers: { 'Authorization': 'Bearer ' + token },
        body: formData
      });
      
      if (res.ok) {
        toast.success(editEvent ? 'رویداد با موفقیت بروزرسانی شد' : 'رویداد با موفقیت ایجاد شد');
        fetchEvents();
        handleCloseModal();
      } else {
        const error = await res.json();
        toast.error(error.error || 'خطا در ذخیره رویداد');
      }
    } catch (err) {
      console.error('Error saving event:', err);
      toast.error('خطا در ذخیره رویداد');
    }
    setSaving(false);
  };

  const formatDate = (dateString) => {
    return moment(dateString).format('jYYYY/jMM/jDD HH:mm');
  };

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" component="h1" sx={{ fontFamily: 'Vazir' }}>
          مدیریت رویدادها و کارگاه‌ها
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => handleOpenModal()}
          sx={{ fontFamily: 'Vazir' }}
        >
          افزودن رویداد جدید
        </Button>
      </Box>
      
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell align="center" sx={{ fontFamily: 'Vazir', fontWeight: 'bold' }}>عنوان</TableCell>
              <TableCell align="center" sx={{ fontFamily: 'Vazir', fontWeight: 'bold' }}>تاریخ</TableCell>
              <TableCell align="center" sx={{ fontFamily: 'Vazir', fontWeight: 'bold' }}>مکان</TableCell>
              <TableCell align="center" sx={{ fontFamily: 'Vazir', fontWeight: 'bold' }}>عملیات</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow><TableCell colSpan={4} align="center">در حال بارگذاری...</TableCell></TableRow>
            ) : events?.length === 0 ? (
              <TableRow><TableCell colSpan={4} align="center">رویدادی وجود ندارد.</TableCell></TableRow>
            ) : events?.map(event => (
              <TableRow key={event.id}>
                <TableCell align="center" sx={{ fontFamily: 'Vazir' }}>{event.title}</TableCell>
                <TableCell align="center" sx={{ fontFamily: 'Vazir' }}>{formatDate(event.event_date)}</TableCell>
                <TableCell align="center" sx={{ fontFamily: 'Vazir' }}>{event.location}</TableCell>
                <TableCell align="center">
                  <IconButton color="primary" onClick={() => handleOpenModal(event)}><EditIcon /></IconButton>
                  <IconButton color="error" onClick={() => handleDelete(event.id)}><DeleteIcon /></IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      
      <Dialog open={modalOpen} onClose={handleCloseModal} maxWidth="md" fullWidth>
        <DialogTitle sx={{ fontFamily: 'Vazir' }}>
          {editEvent ? 'ویرایش رویداد' : 'افزودن رویداد جدید'}
        </DialogTitle>
        <form onSubmit={handleSubmit}>
          <DialogContent>
            <TextField
              fullWidth
              label="عنوان رویداد"
              value={form.title}
              onChange={(e) => setForm({...form, title: e.target.value})}
              margin="normal"
              required
              sx={{ fontFamily: 'Vazir' }}
            />
            
            <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
              <TextField
                label="سال"
                type="number"
                value={form.event_year}
                onChange={(e) => setForm({...form, event_year: parseInt(e.target.value)})}
                margin="normal"
                sx={{ fontFamily: 'Vazir', minWidth: 100 }}
                inputProps={{ min: 1400, max: 1500 }}
              />
              <TextField
                label="ماه"
                type="number"
                value={form.event_month}
                onChange={(e) => setForm({...form, event_month: parseInt(e.target.value)})}
                margin="normal"
                sx={{ fontFamily: 'Vazir', minWidth: 100 }}
                inputProps={{ min: 1, max: 12 }}
              />
              <TextField
                label="روز"
                type="number"
                value={form.event_day}
                onChange={(e) => setForm({...form, event_day: parseInt(e.target.value)})}
                margin="normal"
                sx={{ fontFamily: 'Vazir', minWidth: 100 }}
                inputProps={{ min: 1, max: 31 }}
              />
            </Box>
            
            <LocalizationProvider dateAdapter={AdapterMoment}>
              <TimePicker
                label="زمان رویداد"
                value={form.event_time}
                onChange={(newValue) => setForm({...form, event_time: newValue})}
                renderInput={(params) => <TextField {...params} fullWidth margin="normal" sx={{ fontFamily: 'Vazir' }} />}
                ampm={false}
              />
            </LocalizationProvider>
            
            <TextField
              fullWidth
              label="مکان رویداد"
              value={form.location}
              onChange={(e) => setForm({...form, location: e.target.value})}
              margin="normal"
              required
              sx={{ fontFamily: 'Vazir' }}
            />
            
            <TextField
              fullWidth
              label="توضیحات"
              value={form.description}
              onChange={(e) => setForm({...form, description: e.target.value})}
              margin="normal"
              multiline
              rows={4}
              sx={{ fontFamily: 'Vazir' }}
            />
            
            <TextField
              fullWidth
              label="لینک ثبت نام"
              value={form.registration_link}
              onChange={(e) => setForm({...form, registration_link: e.target.value})}
              margin="normal"
              sx={{ fontFamily: 'Vazir' }}
            />
            
            <TextField
              fullWidth
              label="پوستر رویداد"
              type="file"
              inputProps={{ accept: 'image/*' }}
              onChange={(e) => setPosterFile(e.target.files[0])}
              margin="normal"
              sx={{ fontFamily: 'Vazir' }}
            />
            
            <TextField
              fullWidth
              label="عنوان SEO"
              value={form.meta_title}
              onChange={(e) => setForm({...form, meta_title: e.target.value})}
              margin="normal"
              sx={{ fontFamily: 'Vazir' }}
            />
            
            <TextField
              fullWidth
              label="توضیحات SEO"
              value={form.meta_description}
              onChange={(e) => setForm({...form, meta_description: e.target.value})}
              margin="normal"
              multiline
              rows={2}
              sx={{ fontFamily: 'Vazir' }}
            />
            
            <TextField
              fullWidth
              label="کلمات کلیدی SEO"
              value={form.meta_keywords}
              onChange={(e) => setForm({...form, meta_keywords: e.target.value})}
              margin="normal"
              sx={{ fontFamily: 'Vazir' }}
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseModal} sx={{ fontFamily: 'Vazir' }}>انصراف</Button>
            <Button type="submit" variant="contained" disabled={saving} sx={{ fontFamily: 'Vazir' }}>
              {saving ? 'در حال ذخیره...' : (editEvent ? 'بروزرسانی' : 'ایجاد')}
            </Button>
          </DialogActions>
        </form>
      </Dialog>
      
      <Fab
        color="primary"
        aria-label="add"
        onClick={() => handleOpenModal()}
        sx={{ 
          position: 'fixed', 
          bottom: 16, 
          right: 16,
          zIndex: 1000,
          backgroundColor: '#1976d2',
          '&:hover': {
            backgroundColor: '#1565c0',
          }
        }}
      >
        <AddIcon />
      </Fab>
    </Box>
  );
}

export default Events;
